export function isFormInteractionActive(root: ParentNode): boolean {
  if (root.querySelector('[role="dialog"] form')) return true;
  // Inline support replies (including selected files) survive snapshot traffic
  // even after focus moves to another control.
  for (const form of root.querySelectorAll<HTMLFormElement>(
    ".support-detail form, #shdeed-support-create-form",
  )) {
    for (const field of form.querySelectorAll<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >("input, textarea, select")) {
      if (field instanceof HTMLInputElement && field.type === "file" && field.files?.length)
        return true;
      if (
        (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) &&
        field.type !== "hidden" &&
        field.value !== field.defaultValue
      )
        return true;
      if (field instanceof HTMLSelectElement) {
        const defaults = [...field.options].map((option, index) =>
          option.defaultSelected ? index : -1,
        );
        const defaultIndex = Math.max(0, ...defaults);
        if (field.selectedIndex !== defaultIndex) return true;
      }
    }
  }
  const focused = document.activeElement;
  return (
    focused instanceof HTMLElement &&
    root.contains(focused) &&
    focused.matches('input, textarea, select, [contenteditable="true"]')
  );
}

type SaveContext = {
  scope: () => string;
  locale: () => string;
  error: (form: HTMLFormElement) => string;
  saved: () => void;
};

function formFeedback(form: HTMLFormElement): HTMLElement {
  const feedback =
    form.closest('[role="dialog"]')?.querySelector<HTMLElement>("[data-form-feedback]") ??
    document.createElement("p");
  if (!feedback.isConnected) form.before(feedback);
  feedback.className = "form-feedback";
  feedback.hidden = false;
  if (feedback.parentElement?.matches('[role="dialog"]')) {
    feedback.parentElement.classList.add("has-form-feedback");
  }
  return feedback;
}

export function bindFormValidationFeedback(root: HTMLElement, locale: () => string): void {
  if (root.dataset.formValidationBound) return;
  root.dataset.formValidationBound = "true";
  root.addEventListener(
    "invalid",
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLElement)) return;
      const form = input.closest("form");
      if (!form || !form.closest('[role="dialog"]')) return;
      const feedback = formFeedback(form);
      feedback.classList.add("error");
      feedback.setAttribute("role", "alert");
      feedback.textContent =
        locale() === "ar"
          ? "راجع الحقول المحددة وأكمل البيانات المطلوبة قبل الحفظ."
          : "Check the highlighted fields and complete the required information before saving.";
    },
    true,
  );
}

/** Keep the submitted DOM alive: FormData, file inputs and failed drafts depend on it. */
export function createFormSaveLifecycle(context: SaveContext): {
  holdsRender: () => boolean;
  run: (form: HTMLFormElement, operation: () => Promise<boolean>) => Promise<void>;
} {
  let pending: { form: HTMLFormElement; scope: string } | undefined;
  return {
    holdsRender: () => !!pending?.form.isConnected && pending.scope === context.scope(),
    async run(form, operation) {
      if (pending) {
        if (pending.form !== form) {
          const notice = formFeedback(form);
          notice.setAttribute("role", "status");
          notice.textContent =
            context.locale() === "ar"
              ? "لا تزال عملية الحفظ السابقة قيد التنفيذ. انتظر اكتمالها ثم حاول مرة أخرى."
              : "The previous save is still in progress. Wait for it to finish, then try again.";
        }
        return;
      }
      const current = { form, scope: context.scope() };
      pending = current;
      const feedback = formFeedback(form);
      feedback.setAttribute("role", "status");
      feedback.textContent = context.locale() === "ar" ? "جارٍ الحفظ…" : "Saving…";
      form.setAttribute("aria-busy", "true");
      form.inert = true;
      let saved = false;
      let failure = "";
      try {
        saved = await operation();
        if (!saved) failure = context.error(form);
      } catch {
        // Existing save handlers own detailed, sanitized errors. Never expose raw provider output.
        failure = context.error(form);
      } finally {
        if (pending === current) pending = undefined;
        form.inert = false;
        form.removeAttribute("aria-busy");
      }
      if (!form.isConnected || current.scope !== context.scope()) return;
      if (saved) {
        context.saved();
      } else {
        feedback.classList.add("error");
        feedback.setAttribute("role", "alert");
        feedback.tabIndex = -1;
        feedback.textContent =
          failure ||
          (context.locale() === "ar"
            ? "لم يتم الحفظ. تحقق من الاتصال وحاول مرة أخرى."
            : "Could not save. Check your connection and try again.");
        feedback.focus({ preventScroll: true });
      }
    },
  };
}
